import Quill from 'quill';
import {
  addTableColumn,
  addTableHeader,
  addTableRow,
  applyTableDataToNode,
  deleteTableColumn,
  deleteTableRow,
  emptyTableData,
  htmlSelectionToLines,
  MAX_COL_WIDTH,
  MIN_COL_WIDTH,
  readTableFromDom,
  renderTableHtml,
  setTableColWidth,
  textToTableData,
  withCheckboxPrefix,
} from '../../utils/noteTableUtils';

export const NOTE_TABLE_BLOT = 'noteTable';

const blotByQuill = new WeakMap();
const lastTableCellByEditor = new WeakMap();

export const getLastNoteTableCell = (editor) => {
  const cell = lastTableCellByEditor.get(editor);
  if (cell && editor?.root?.contains(cell)) return cell;
  const focused = typeof document !== 'undefined'
    ? document.activeElement?.closest?.('th[data-r], td[data-r]')
    : null;
  if (focused && editor?.root?.contains(focused)) return focused;
  return null;
};

export const registerNoteTableBlot = (QuillCtor = Quill) => {
  if (!QuillCtor) return null;
  if (blotByQuill.has(QuillCtor)) return blotByQuill.get(QuillCtor);
  try {
    const BlockEmbed = QuillCtor.import('blots/block/embed');
    class NoteTableBlot extends BlockEmbed {
      static create(value) {
        const node = super.create();
        const data = value && Array.isArray(value.rows) ? value : emptyTableData(3, 3);
        node.setAttribute('contenteditable', 'false');
        node.setAttribute('data-table', JSON.stringify(data));
        node.innerHTML = renderTableHtml(data);
        return node;
      }

      static value(node) {
        return readTableFromDom(node);
      }
    }
    NoteTableBlot.blotName = NOTE_TABLE_BLOT;
    NoteTableBlot.tagName = 'DIV';
    NoteTableBlot.className = 'note-data-table';
    QuillCtor.register(NoteTableBlot, true);
    try {
      QuillCtor.register(`formats/${NOTE_TABLE_BLOT}`, NoteTableBlot, true);
    } catch {
      // Quill 1 has no formats/ shortcut
    }
    blotByQuill.set(QuillCtor, NoteTableBlot);
    return NoteTableBlot;
  } catch (err) {
    console.warn('Could not register note table blot', err);
    return null;
  }
};

export const ensureNoteTableOnEditor = (editor) => {
  if (!editor) return null;
  const Q = editor.constructor || Quill;
  const blot = registerNoteTableBlot(Q);
  const registry = editor.scroll?.registry;
  if (blot && registry && typeof registry.register === 'function') {
    try {
      if (typeof registry.query !== 'function' || !registry.query(NOTE_TABLE_BLOT)) {
        registry.register(blot);
      }
    } catch {
      registry.register(blot);
    }
  }
  return blot;
};

export const syncTableNode = (node) => {
  if (!node) return emptyTableData(1, 1);
  const data = readTableFromDom(node);
  node.setAttribute('data-table', JSON.stringify(data));
  return data;
};

export const quillSelectionToTableData = (editor, range) => {
  if (!editor || !range?.length) return null;
  const lines = [];
  let index = range.index;
  const end = range.index + range.length;
  let guard = 0;
  while (index < end && guard < 500) {
    guard += 1;
    const lineInfo = editor.getLine(index);
    const line = Array.isArray(lineInfo) ? lineInfo[0] : null;
    const offset = Array.isArray(lineInfo) ? lineInfo[1] : 0;
    if (!line) {
      lines.push(editor.getText(index, end - index));
      break;
    }
    const lineStart = typeof editor.getIndex === 'function'
      ? editor.getIndex(line)
      : Math.max(0, index - offset);
    const lineLen = typeof line.length === 'function' ? line.length() : 1;
    const textEnd = lineStart + Math.max(0, lineLen - 1);
    const takeStart = Math.max(lineStart, range.index);
    const takeEnd = Math.min(textEnd, end);
    const text = takeEnd > takeStart
      ? String(editor.getText(takeStart, takeEnd - takeStart) || '').replace(/\u00a0/g, ' ')
      : '';
    let listValue = line.domNode?.getAttribute?.('data-list') || '';
    try {
      listValue = editor.getFormat(lineStart, 1)?.list || listValue;
    } catch {
      // keep attribute
    }
    lines.push(withCheckboxPrefix(listValue, text.replace(/\s+$/g, '')));
    const nextIndex = lineStart + lineLen;
    index = nextIndex > index ? nextIndex : end;
  }
  if (lines.some((line) => String(line || '').trim())) {
    return textToTableData(lines.join('\n'));
  }
  if (typeof editor.getSemanticHTML === 'function') {
    try {
      const htmlLines = htmlSelectionToLines(editor.getSemanticHTML(range.index, range.length));
      if (htmlLines.some((line) => line.trim())) {
        return textToTableData(htmlLines.join('\n'));
      }
    } catch {
      // ignored
    }
  }
  return null;
};

export const insertNoteTableAtSelection = (editor, data) => {
  if (!editor) return;
  ensureNoteTableOnEditor(editor);
  const range = editor.getSelection(true) || { index: Math.max(0, editor.getLength() - 1), length: 0 };
  let index = range.index;
  if (range.length) {
    editor.deleteText(index, range.length, 'silent');
  }
  if (typeof editor.format === 'function') {
    editor.format('list', false, 'silent');
    editor.format('code-block', false, 'silent');
    editor.format('blockquote', false, 'silent');
    editor.format('header', false, 'silent');
  }
  const lineInfo = editor.getLine(index);
  const offset = Array.isArray(lineInfo) ? lineInfo[1] : 0;
  if (offset > 0) {
    editor.insertText(index, '\n', 'silent');
    index += 1;
    if (typeof editor.format === 'function') {
      editor.format('list', false, 'silent');
    }
  }
  try {
    editor.insertEmbed(index, NOTE_TABLE_BLOT, data, 'user');
  } catch (err) {
    editor.insertText(editor.getLength() - 1, '\n', 'silent');
    index = Math.max(0, editor.getLength() - 1);
    editor.insertEmbed(index, NOTE_TABLE_BLOT, data, 'user');
  }
  const after = index + 1;
  const nextChar = editor.getText(after, 1);
  if (nextChar !== '\n') {
    editor.insertText(after, '\n', 'silent');
  }
  editor.setSelection(after + (nextChar === '\n' ? 0 : 1), 0, 'silent');
};

export const attachNoteTableChrome = (editor, onSummary, onChange) => {
  if (!editor?.root) return () => {};
  let lastCell = null;

  const onInput = (event) => {
    const cell = event.target?.closest?.('th,td');
    const table = cell?.closest?.('.note-data-table');
    if (!table || !editor.root.contains(table)) return;
    syncTableNode(table);
    onChange?.();
  };

  const onKeyDown = (event) => {
    const cell = event.target?.closest?.('th,td');
    if (!cell || !editor.root.contains(cell)) return;
    event.stopPropagation();
    if (event.key !== 'Enter') return;
    event.preventDefault();
    document.execCommand('insertLineBreak');
  };

  const onBlur = (event) => {
    const cell = event.target?.closest?.('th,td');
    const table = cell?.closest?.('.note-data-table');
    if (!table || !editor.root.contains(table)) return;
    syncTableNode(table);
    onChange?.();
  };

  const rememberCell = (cell) => {
    lastCell = cell || null;
    if (cell && editor.root.contains(cell)) lastTableCellByEditor.set(editor, cell);
    else lastTableCellByEditor.delete(editor);
  };

  const onFocusIn = (event) => {
    const cell = event.target?.closest?.('th,td');
    const table = cell?.closest?.('.note-data-table');
    if (!table || !editor.root.contains(table)) return;
    rememberCell(cell);
  };

  const onEditorPointerDown = (event) => {
    const cell = event.target?.closest?.('th,td');
    if (cell && editor.root.contains(cell)) {
      rememberCell(cell);
      return;
    }
    if (event.target?.closest?.('.note-data-table')) return;
    rememberCell(null);
  };

  const onChangeBox = (event) => {
    const input = event.target;
    if (!input || input.type !== 'checkbox') return;
    const label = input.closest('label.note-cell-check');
    const table = input.closest('.note-data-table');
    if (!table || !editor.root.contains(table)) return;
    label?.classList.toggle('is-checked', input.checked);
    syncTableNode(table);
    onChange?.();
  };

  const onClick = (event) => {
    const button = event.target?.closest?.('[data-table-action]');
    if (!button) return;
    const table = button.closest('.note-data-table');
    if (!table || !editor.root.contains(table)) return;
    event.preventDefault();
    event.stopPropagation();
    const action = button.getAttribute('data-table-action');
    const current = syncTableNode(table);
    if (action === 'summary') {
      onSummary?.(current, table);
      return;
    }
    const sourceCell = lastCell && table.contains(lastCell) ? lastCell : null;
    const rowIndex = Number.parseInt(sourceCell?.getAttribute('data-r'), 10);
    const colIndex = Number.parseInt(sourceCell?.getAttribute('data-c'), 10);
    const next = action === 'add-header'
      ? addTableHeader(current)
      : action === 'add-row'
        ? addTableRow(current, Number.isFinite(rowIndex) ? rowIndex : null)
        : action === 'add-col'
          ? addTableColumn(current, Number.isFinite(colIndex) ? colIndex : null)
          : action === 'del-row'
            ? deleteTableRow(current, Number.isFinite(rowIndex) ? rowIndex : current.rows.length - 1)
            : action === 'del-col'
              ? deleteTableColumn(current, Number.isFinite(colIndex) ? colIndex : (current.rows[0] || []).length - 1)
              : current;
    applyTableDataToNode(table, next);
    onChange?.();
    const focusRow = action === 'add-header'
      ? 0
      : action === 'add-row'
        ? (Number.isFinite(rowIndex) ? rowIndex + 1 : next.rows.length - 1)
        : (Number.isFinite(rowIndex) ? rowIndex : 0);
    const focusCol = action === 'add-header' && current.hasHeader
      ? Math.max(0, (next.rows[0] || []).length - 1)
      : action === 'add-col'
        ? (Number.isFinite(colIndex) ? colIndex + 1 : (next.rows[0] || []).length - 1)
        : (Number.isFinite(colIndex) ? colIndex : 0);
    const focusCell = table.querySelector(`[data-r="${focusRow}"][data-c="${focusCol}"]`);
    rememberCell(focusCell);
    focusCell?.focus?.();
  };

  const onResizeDown = (event) => {
    const handle = event.target?.closest?.('.note-col-resizer');
    if (!handle) return;
    const tableWrap = handle.closest('.note-data-table');
    if (!tableWrap || !editor.root.contains(tableWrap)) return;
    const colIndex = Number.parseInt(handle.getAttribute('data-c'), 10);
    if (!Number.isFinite(colIndex)) return;
    event.preventDefault();
    event.stopPropagation();
    const tableEl = tableWrap.querySelector('table.note-table');
    const colEl = tableEl?.querySelector(`col[data-c="${colIndex}"]`);
    const startX = event.clientX;
    const startW = (colEl?.getBoundingClientRect().width)
      || Number.parseFloat(colEl?.style.width)
      || MIN_COL_WIDTH;
    const applyWidth = (width) => {
      const nextWidth = Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, Math.round(width)));
      if (colEl) colEl.style.width = `${nextWidth}px`;
      tableWrap.querySelectorAll(`th[data-c="${colIndex}"], td[data-c="${colIndex}"]`).forEach((cell) => {
        cell.style.width = `${nextWidth}px`;
      });
      if (tableEl) {
        const total = [...tableEl.querySelectorAll('col')].reduce((sum, col) => (
          sum + (Number.parseFloat(col.style.width) || MIN_COL_WIDTH)
        ), 0);
        tableEl.style.width = `${total}px`;
      }
      return nextWidth;
    };
    const onMove = (moveEvent) => {
      applyWidth(startW + moveEvent.clientX - startX);
    };
    const onUp = (upEvent) => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('mouseup', onUp, true);
      const width = applyWidth(startW + upEvent.clientX - startX);
      const current = syncTableNode(tableWrap);
      const next = setTableColWidth(current, colIndex, width);
      tableWrap.setAttribute('data-table', JSON.stringify(next));
      onChange?.();
    };
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('mouseup', onUp, true);
  };

  editor.root.addEventListener('input', onInput, true);
  editor.root.addEventListener('keydown', onKeyDown, true);
  editor.root.addEventListener('focusin', onFocusIn, true);
  editor.root.addEventListener('focusout', onBlur, true);
  editor.root.addEventListener('change', onChangeBox, true);
  editor.root.addEventListener('click', onClick, true);
  editor.root.addEventListener('mousedown', onEditorPointerDown, true);
  editor.root.addEventListener('mousedown', onResizeDown, true);
  return () => {
    editor.root.removeEventListener('input', onInput, true);
    editor.root.removeEventListener('keydown', onKeyDown, true);
    editor.root.removeEventListener('focusin', onFocusIn, true);
    editor.root.removeEventListener('focusout', onBlur, true);
    editor.root.removeEventListener('change', onChangeBox, true);
    editor.root.removeEventListener('click', onClick, true);
    editor.root.removeEventListener('mousedown', onEditorPointerDown, true);
    editor.root.removeEventListener('mousedown', onResizeDown, true);
  };
};
